import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowRight, Heart, BookOpen, Video, GraduationCap, Tv, Calendar, Handshake,
  MessageCircle, Compass, Lightbulb, Users, Rocket,
  HandHeart, Package, Droplets, Building2,
  Instagram, Youtube, CalendarDays, Mic, BookMarked, MonitorPlay, ExternalLink,
  School, Brain, BookOpenCheck, Award, Target, Medal, Network,
  Palette, Puzzle, Tent, Camera,
  Link2, Megaphone, Briefcase, BarChart3,
  Clapperboard, PenLine, Headphones, PenTool,
  Globe,
  Moon,
  MapPin,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { flyers } from './belajar-islam/Infografis';

/* ─── Shared Donation View Component ─── */
const DonationView = ({ onBack, backLabel }) => (
  <>
    <DialogHeader>
      <DialogTitle className="text-2xl font-serif font-bold text-gold mb-4 text-center">Salurkan Donasi Terbaik Anda</DialogTitle>
    </DialogHeader>
    <div className="p-4 text-center space-y-6">
      <div className="rounded-xl overflow-hidden border border-gold/20 max-w-[180px] mx-auto">
        <img
          src="https://customer-assets.emergentagent.com/job_clean-menu-design/artifacts/wik7abko_image.png"
          alt="QRIS Donasi"
          className="w-full h-auto"
        />
      </div>
      <div className="bg-white/5 p-4 rounded-xl border border-white/10">
        <p className="text-sm text-muted-foreground mb-1">Transfer Manual</p>
        <p className="text-sm text-gold font-medium mb-1">Bank Syariah Indonesia (BSI)</p>
        <div className="flex items-center justify-center gap-2 mb-1">
          <p className="text-2xl font-mono font-bold text-white tracking-wider select-all">7741114474</p>
        </div>
        <p className="text-xs text-muted-foreground">an. Yayasan Thuba Minal Jannah</p>
      </div>
      <a
        href="https://wa.me/6282310001447?text=Assalamu'alaikum%2C%20saya%20sudah%20transfer%20donasi%20untuk%20Thuba%20Project"
        target="_blank"
        rel="noopener noreferrer"
        className="block"
      >
        <Button className="bg-primary text-black hover:bg-[#F3E5AB] font-bold w-full rounded-full">
          Konfirmasi via WhatsApp
        </Button>
      </a>
      <button
        onClick={onBack}
        className="text-sm text-muted-foreground hover:text-white transition-colors"
      >
        ← Kembali ke info {backLabel}
      </button>
    </div>
  </>
);

/* ─── Shared CTA (Donasi + Relawan) ─── */
const SharedCTA = ({ onDonate }) => (
  <div className="space-y-3 pt-4 border-t border-white/10">
    <div className="flex flex-col sm:flex-row gap-3">
      <Button
        onClick={onDonate}
        className="flex-1 bg-primary text-black hover:bg-[#F3E5AB] font-bold py-5 rounded-xl shadow-lg shadow-gold/20"
      >
        <Heart className="w-4 h-4 mr-2" /> Donasi
      </Button>
      <a
        href="https://wa.me/6282310001447?text=Assalamualaikum%2C%20Insyallah%20saya%20bersedia%20menjadi%20Relawan%20melalui%20sebab%20Thuba%20Project"
        target="_blank"
        rel="noopener noreferrer"
        className="flex-1"
      >
        <Button className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-5 rounded-xl shadow-lg shadow-green-900/20">
          <MessageCircle className="w-4 h-4 mr-2" /> Gabung Relawan
        </Button>
      </a>
    </div>
  </div>
);

/* ─── Section Header ─── */
const EcoSectionHeader = ({ icon: Icon, children }) => (
  <h3 className="text-lg font-serif font-semibold text-white flex items-center gap-3">
    <Icon className="w-5 h-5 text-gold" /> {children}
  </h3>
);

/* ─── Program List ─── */
const EcoProgramList = ({ items }) => (
  <div className="space-y-2 bg-white/5 p-4 rounded-xl border border-white/10">
    {items.map((item, i) => (
      <div key={i} className="flex items-center gap-3 text-muted-foreground text-sm">
        <item.icon className="w-4 h-4 text-gold/70 flex-shrink-0" />
        <span>{item.text}</span>
      </div>
    ))}
  </div>
);

/* ─── Tag Grid ─── */
const EcoTagGrid = ({ items, cols = 2 }) => (
  <div className={`grid grid-cols-${cols} gap-2`}>
    {items.map((item, i) => (
      <div key={i} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-center text-muted-foreground text-sm">
        {item}
      </div>
    ))}
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const [programs, setPrograms] = useState([]);
  const flyerSliderRef = useRef(null);
  const [isFlyerHovered, setIsFlyerHovered] = useState(false);
  const [flyerLightbox, setFlyerLightbox] = useState(null); // { idx }

  // Keyboard navigation in flyer lightbox
  useEffect(() => {
    if (!flyerLightbox) return;
    const handler = (e) => {
      if (e.key === 'ArrowLeft') setFlyerLightbox(lb => ({ idx: (lb.idx - 1 + flyers.length) % flyers.length }));
      if (e.key === 'ArrowRight') setFlyerLightbox(lb => ({ idx: (lb.idx + 1) % flyers.length }));
      if (e.key === 'Escape') setFlyerLightbox(null);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [flyerLightbox]);

  // Auto-scroll logic for Flayer Dakwah slider
  useEffect(() => {
    let interval;
    if (!isFlyerHovered) {
      interval = setInterval(() => {
        if (flyerSliderRef.current) {
          flyerSliderRef.current.scrollLeft += 1;

          // Reset to beginning if reached the end smoothly
          if (flyerSliderRef.current.scrollLeft >= (flyerSliderRef.current.scrollWidth - flyerSliderRef.current.clientWidth - 1)) {
            flyerSliderRef.current.scrollLeft = 0;
          }
        }
      }, 30);
    }
    return () => clearInterval(interval);
  }, [isFlyerHovered]);

  const scrollFlyerLeft = () => {
    if (flyerSliderRef.current) {
      flyerSliderRef.current.scrollBy({ left: -320, behavior: 'smooth' });
    }
  };

  const scrollFlyerRight = () => {
    if (flyerSliderRef.current) {
      flyerSliderRef.current.scrollBy({ left: 320, behavior: 'smooth' });
    }
  };
  const [currentHeroIndex, setCurrentHeroIndex] = useState(0);
  const [currentActivityIndex, setCurrentActivityIndex] = useState(0);
  const [activeEcoDialog, setActiveEcoDialog] = useState(null);
  const [ecoDonationView, setEcoDonationView] = useState(false);
  const [mainDonasiOpen, setMainDonasiOpen] = useState(false);
  const [activeGalleryTab, setActiveGalleryTab] = useState('sosial');
  const [galleryLightbox, setGalleryLightbox] = useState(null); // { images, idx }
  const touchStartRef = useRef(null);

  const sosialImages = [
    { src: '/sosial%201/photo_2026-05-24_16-19-32.jpg', alt: 'Penyaluran Sembako Thuba Sosial' },
    { src: '/sosial%201/photo_2026-05-24_16-19-34.jpg', alt: 'Berbagi Sembako 2' },
    { src: '/sosial%201/photo_2026-05-24_16-28-42.jpg', alt: 'Berbagi Sembako 3' },
    { src: '/sosial%201/photo_2026-05-24_16-59-30.jpg', alt: 'Berbagi Sembako 4' },
    { src: '/sosial%201/photo_2026-05-24_17-02-06.jpg', alt: 'Berbagi Sembako 5' },
    { src: '/sosial%201/2026-06-10%2023_21_21.jpg', alt: 'Berbagi Sembako 6' },
  ];

  const dawahImages = [
    { src: '/buruhmengaji/IMG_20260510_080757.jpg', alt: 'Kajian Thuba Da\'wah bersama Buruh Mengaji' },
    { src: '/buruhmengaji/IMG_20260510_091310.jpg', alt: 'Buruh Mengaji 2' },
    { src: '/buruhmengaji/IMG_20260510_091424.jpg', alt: 'Buruh Mengaji 3' },
    { src: '/buruhmengaji/IMG_20260510_091527.jpg', alt: 'Buruh Mengaji 4' },
    { src: '/buruhmengaji/2026-06-10%2022_53_14.jpg', alt: 'Buruh Mengaji 5' },
    { src: '/buruhmengaji/2026-06-10%2022_54_44.jpg', alt: 'Buruh Mengaji 6' },
  ];

  const openGalleryLightbox = (images, idx) => setGalleryLightbox({ images, idx });
  const closeGalleryLightbox = () => setGalleryLightbox(null);
  const galleryPrev = () => setGalleryLightbox(lb => ({ ...lb, idx: (lb.idx - 1 + lb.images.length) % lb.images.length }));
  const galleryNext = () => setGalleryLightbox(lb => ({ ...lb, idx: (lb.idx + 1) % lb.images.length }));

  // Keyboard nav for gallery lightbox
  useEffect(() => {
    if (!galleryLightbox) return;
    const handler = (e) => {
      if (e.key === 'ArrowLeft') galleryPrev();
      if (e.key === 'ArrowRight') galleryNext();
      if (e.key === 'Escape') closeGalleryLightbox();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [galleryLightbox]);

  const handleTouchStart = (e) => { touchStartRef.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartRef.current === null) return;
    const diff = touchStartRef.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) { diff > 0 ? galleryNext() : galleryPrev(); }
    touchStartRef.current = null;
  };

  useEffect(() => {
    const onPopState = (e) => {
      if (e.state?.dialog) {
        if (e.state.dialog === 'main-donasi') {
          setMainDonasiOpen(true);
        } else {
          setActiveEcoDialog(e.state.dialog);
          setEcoDonationView(false);
        }
      } else {
        setActiveEcoDialog(null);
        setEcoDonationView(false);
        setMainDonasiOpen(false);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const openEcoDialog = (id) => {
    setActiveEcoDialog(id);
    setEcoDonationView(false);
    window.history.pushState({ dialog: id }, '');
  };

  const closeEcoDialog = () => {
    setActiveEcoDialog(null);
    setEcoDonationView(false);
    if (window.history.state?.dialog) {
      window.history.back();
    }
  };

  const openMainDonasiDialog = () => {
    setMainDonasiOpen(true);
    window.history.pushState({ dialog: 'main-donasi' }, '');
  };

  const closeMainDonasiDialog = () => {
    setMainDonasiOpen(false);
    if (window.history.state?.dialog) {
      window.history.back();
    }
  };

  // Hero Background Images
  const heroImages = [
    "/ustadz1.jpg",
    "/ustadz2.jpg",
    "/gambar 3.jpeg",
    "/gambar 4.jpg",
    "/UB.jpg",
    "https://images.unsplash.com/photo-1741091354879-64cf2c3bc069?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBtb3NxdWUlMjBhcmNoaXRlY3R1cmUlMjBkYXJrJTIwZ29sZCUyMGxpZ2h0aW5nfGVufDB8fHx8MTc2OTk4OTk4MHww&ixlib=rb-4.1.0&q=85"
  ];

  // Activity Images (Floating)
  const activityImages = [
    "/ustadz1.jpg",
    "/ustadz2.jpg",
    "/gambar 3.jpeg",
    "/gambar 4.jpg",
    "/UB.jpg"
  ];

  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/programs`);
        setPrograms(res.data.slice(0, 2)); // Show top 2
      } catch (err) {
        console.error("Failed to fetch programs", err);
      }
    };
    fetchPrograms();
  }, []);

  // Auto slide effect for Hero Background
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeroIndex((prev) => (prev + 1) % heroImages.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Auto slide effect for Activity Image
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentActivityIndex((prev) => (prev + 1) % activityImages.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="animate-fade-in-up">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden pt-20">
        {/* Background Slider */}
        <div className="absolute inset-0 z-0">
          {heroImages.map((img, index) => (
            <img
              key={index}
              src={img}
              alt={`Hero Background ${index + 1}`}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${index === currentHeroIndex ? 'opacity-40' : 'opacity-0'
                }`}
            />
          ))}
          <div className="absolute inset-0 bg-gradient-to-b from-[#081c1a] via-[#081c1a]/60 to-[#081c1a]"></div>
        </div>

        <div className="container mx-auto px-6 relative z-10 grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <h1 className="text-6xl md:text-8xl font-serif font-bold leading-tight text-white">
              Faith.<br />
              <span className="text-gold">Focus.</span><br />
              Create.
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-lg font-light leading-relaxed">
              Berakar dari sebuah keyakinan, kami fokus berikhtiar untuk tumbuh dalam dakwah yang menghasilkan amal demi mengharapkan ridho Allah, menguatkan ukhuwah dan kemaslahatan umat.
            </p>
            <div className="flex gap-4 pt-4">
              <Link to="/ecosystem">
                <Button className="bg-primary text-black hover:bg-[#F3E5AB] rounded-full px-8 py-6 text-lg font-medium shadow-[0_0_20px_rgba(212,175,55,0.2)]">
                  Pelajari Lebih Lanjut
                </Button>
              </Link>
            </div>
          </div>

          <div className="hidden md:flex justify-center items-center relative">
            {/* Decorative Elements */}
            <div className="absolute inset-0 bg-gold/20 blur-[100px] rounded-full opacity-20"></div>

            <div className="relative z-10 p-2 border border-gold/30 rounded-2xl bg-white/5 backdrop-blur-sm transform rotate-3 hover:rotate-0 transition-all duration-500 shadow-2xl shadow-gold/10">
              <div className="relative w-[400px] h-[300px] rounded-xl overflow-hidden">
                {activityImages.map((img, index) => (
                  <img
                    key={index}
                    src={img}
                    alt={`Thuba Project Activity ${index + 1}`}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${index === currentActivityIndex ? 'opacity-100' : 'opacity-0'
                      }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Brief - Centered Layout */}
      <section className="py-24 bg-[#0D2B29] relative overflow-hidden">
        <div className="container mx-auto px-6 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-serif font-medium mb-8 text-white">
              Wasilah <span className="text-gold">Kebaikan.</span>
            </h2>
            <div className="space-y-6 text-muted-foreground text-lg mb-8">
              <p>
                Kami hadir sebagai wasilah kebaikan, sebuah ikhtiar dakwah dan sosial yang berlandaskan nilai-nilai Islam yang sesuai Al-Qur’an dan Sunnah dengan pemahaman Salafush Shalih.
              </p>
              <p>
                Kami berupaya mengajak kepada amal shalih, menumbuhkan kepedulian, dan menebar manfaat.
              </p>
            </div>
            <Link to="/about" className="inline-flex items-center text-gold font-semibold border-b-2 border-gold hover:text-white hover:border-white transition-all pb-1">
              Selengkapnya tentang kami <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Ecosystem Preview */}
      <section className="py-24 bg-[#0A2220]">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-serif font-medium mb-4 text-white">Ekosistem Thuba</h2>
            <p className="text-muted-foreground">Bergerak dalam berbagai lini untuk kemaslahatan umat.</p>
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
            {[
              { title: "Thuba Sosial", icon: Heart, desc: "Kepedulian melalui aksi kemanusiaan dan bantuan sosial yang amanah dan berkelanjutan", dialogId: "sosial" },
              { title: "Thuba Da'wah", icon: BookOpen, desc: "Dakwah yang berakar pada ilmu dan sesuai dengan Al-Qur'an dan As Sunnah", dialogId: "dawah" },
              { title: "Thuba Media", icon: Video, desc: "Menyebarkan nilai kebaikan melalui konten media yang kreatif, edukatif, dan mencerahkan", dialogId: "media" },
              { title: "Thuba Pendidikan", icon: GraduationCap, desc: "Menguatkan umat melalui pendidikan, pelatihan, adab dan pembentukan karakter", dialogId: "pendidikan" },
              { title: "Thuba Kidz", icon: Tv, desc: "Menumbuhkan iman dan akhlak anak sejak dini melalui tayangan yang menarik dan bermanfaat", dialogId: "kidz" },
              { title: "Thuba Kemitraan", icon: Handshake, desc: "merupakan ruang kolaborasi dan komersial yang dirancang untuk membangun kerja sama strategis secara profesional, berlandaskan nilai amanah, transparansi, dan kebermanfaatan.", dialogId: "kemitraan" },
            ].map((eco, idx) => (
              <div key={idx} className="glass-card p-6 rounded-xl hover:bg-white/5 group cursor-pointer" onClick={() => openEcoDialog(eco.dialogId)}>
                <eco.icon className="w-10 h-10 text-gold mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-lg font-serif font-semibold text-white mb-2">{eco.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{eco.desc}</p>
              </div>
            ))}
          </div>

          {/* ── Ecosystem Dialogs ── */}

          {/* Thuba Sosial */}
          <Dialog open={activeEcoDialog === 'sosial'} onOpenChange={(open) => !open && closeEcoDialog()}>
            <DialogContent className="glass-card border-gold/30 max-w-lg overflow-y-auto max-h-[90vh]">
              {!ecoDonationView ? (
                <>
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-serif font-bold text-gold text-center">Thuba Sosial</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-6 px-1">
                    <p className="text-center text-muted-foreground italic">Berbagi kebaikan, menguatkan kepedulian.</p>
                    <div className="space-y-2">
                      <EcoSectionHeader icon={Compass}>Tentang</EcoSectionHeader>
                      <p className="text-muted-foreground text-sm leading-relaxed">
                        Thuba Sosial adalah ekosistem yang bergerak dalam aksi kemanusiaan, bantuan sosial, dan program pemberdayaan masyarakat. Fokusnya bukan hanya memberi, tetapi membangun keberlanjutan.
                      </p>
                    </div>
                    <div className="space-y-3">
                      <EcoSectionHeader icon={Lightbulb}>Perencanaan Program</EcoSectionHeader>
                      <EcoProgramList items={[
                        { icon: HandHeart, text: "Santunan yatim & dhuafa" },
                        { icon: Package, text: "Program berbagi pangan & paket sembako" },
                        { icon: Droplets, text: "Bantuan bencana & tanggap darurat" },
                        { icon: Building2, text: "Program pemberdayaan ekonomi kecil" },
                      ]} />
                    </div>
                    <div className="space-y-3">
                      <EcoSectionHeader icon={Users}>Siapa yang Bisa Terlibat</EcoSectionHeader>
                      <EcoTagGrid items={["Donatur individu", "Relawan", "Perusahaan mitra CSR", "Komunitas sosial"]} />
                    </div>
                    <SharedCTA onDonate={() => setEcoDonationView(true)} />
                  </div>
                </>
              ) : (
                <DonationView onBack={() => setEcoDonationView(false)} backLabel="Thuba Sosial" />
              )}
            </DialogContent>
          </Dialog>

          {/* Thuba Da'wah */}
          <Dialog open={activeEcoDialog === 'dawah'} onOpenChange={(open) => !open && closeEcoDialog()}>
            <DialogContent className="glass-card border-gold/30 max-w-lg overflow-y-auto max-h-[90vh]">
              {!ecoDonationView ? (
                <>
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-serif font-bold text-gold text-center">Thuba Da'wah</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-6 px-1">
                    <p className="text-center text-muted-foreground italic">Menyebarkan ilmu, menumbuhkan iman.</p>
                    <div className="space-y-2">
                      <EcoSectionHeader icon={Compass}>Tentang</EcoSectionHeader>
                      <p className="text-muted-foreground text-sm leading-relaxed">
                        Thuba Dakwah berfokus pada penyebaran ilmu berdasarkan Al-Qur'an dan Sunnah melalui kajian rutin, tabligh akbar, dan pembinaan keislaman.
                      </p>
                    </div>
                    <div className="space-y-3">
                      <EcoSectionHeader icon={BookOpen}>Program</EcoSectionHeader>
                      <EcoProgramList items={[
                        { icon: CalendarDays, text: "Kajian rutin pekanan" },
                        { icon: Mic, text: "Tabligh akbar & seminar" },
                        { icon: BookMarked, text: "Kajian kitab" },
                        { icon: MonitorPlay, text: "Dakwah via media digital" },
                      ]} />
                    </div>
                    <div className="space-y-3">
                      <EcoSectionHeader icon={Rocket}>Ikuti Dakwah Kami</EcoSectionHeader>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <Link to="/media" className="flex-1">
                          <Button className="w-full bg-primary text-black hover:bg-[#F3E5AB] font-bold py-5 rounded-xl shadow-lg shadow-gold/20">
                            <Instagram className="w-5 h-5 mr-2" />
                            Media Thuba
                          </Button>
                        </Link>
                      </div>
                    </div>
                    <SharedCTA onDonate={() => setEcoDonationView(true)} />
                  </div>
                </>
              ) : (
                <DonationView onBack={() => setEcoDonationView(false)} backLabel="Thuba Da'wah" />
              )}
            </DialogContent>
          </Dialog>

          {/* Thuba Media */}
          <Dialog open={activeEcoDialog === 'media'} onOpenChange={(open) => !open && closeEcoDialog()}>
            <DialogContent className="glass-card border-gold/30 max-w-lg overflow-y-auto max-h-[90vh]">
              {!ecoDonationView ? (
                <>
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-serif font-bold text-gold text-center">Thuba Media</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-6 px-1">
                    <p className="text-center text-muted-foreground italic">Faith. Focus. Create.</p>
                    <div className="space-y-2">
                      <EcoSectionHeader icon={Compass}>Tentang</EcoSectionHeader>
                      <p className="text-muted-foreground text-sm leading-relaxed">
                        Ekosistem kreatif yang memproduksi konten edukatif, inspiratif, dan dakwah melalui media digital.
                      </p>
                    </div>
                    <div className="space-y-3">
                      <EcoSectionHeader icon={Clapperboard}>Konten</EcoSectionHeader>
                      <EcoProgramList items={[
                        { icon: MonitorPlay, text: "Video kajian" },
                        { icon: PenLine, text: "Artikel & blog" },
                        { icon: Headphones, text: "Podcast" },
                        { icon: PenTool, text: "Desain visual dakwah" },
                      ]} />
                    </div>
                    <div className="space-y-3">
                      <EcoSectionHeader icon={Rocket}>Media Sosial Kami</EcoSectionHeader>
                      <div className="grid grid-cols-2 gap-3">
                        <a href="https://instagram.com/thubaproject/" target="_blank" rel="noopener noreferrer">
                          <Button className="w-full bg-primary text-black hover:bg-[#F3E5AB] font-bold py-4 rounded-xl shadow-lg shadow-gold/20 text-sm">
                            <Instagram className="w-4 h-4 mr-1" /> Instagram
                          </Button>
                        </a>
                        <a href="https://www.youtube.com/@ThubaProject/streams" target="_blank" rel="noopener noreferrer">
                          <Button className="w-full bg-primary text-black hover:bg-[#F3E5AB] font-bold py-4 rounded-xl shadow-lg shadow-gold/20 text-sm">
                            <Youtube className="w-4 h-4 mr-1" /> YouTube
                          </Button>
                        </a>
                        <a href="https://tiktok.com/@thuba.project" target="_blank" rel="noopener noreferrer">
                          <Button className="w-full bg-primary text-black hover:bg-[#F3E5AB] font-bold py-4 rounded-xl shadow-lg shadow-gold/20 text-sm">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1"><path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" /></svg> TikTok
                          </Button>
                        </a>
                        <a href="https://facebook.com/thubaproject" target="_blank" rel="noopener noreferrer">
                          <Button className="w-full bg-primary text-black hover:bg-[#F3E5AB] font-bold py-4 rounded-xl shadow-lg shadow-gold/20 text-sm">
                            <Globe className="w-4 h-4 mr-1" /> Facebook
                          </Button>
                        </a>
                      </div>
                    </div>
                    <SharedCTA onDonate={() => setEcoDonationView(true)} />
                  </div>
                </>
              ) : (
                <DonationView onBack={() => setEcoDonationView(false)} backLabel="Thuba Media" />
              )}
            </DialogContent>
          </Dialog>

          {/* Thuba Pendidikan */}
          <Dialog open={activeEcoDialog === 'pendidikan'} onOpenChange={(open) => !open && closeEcoDialog()}>
            <DialogContent className="glass-card border-gold/30 max-w-lg overflow-y-auto max-h-[90vh]">
              {!ecoDonationView ? (
                <>
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-serif font-bold text-gold text-center">Thuba Pendidikan</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-6 px-1">
                    <p className="text-center text-muted-foreground italic">Membangun generasi berilmu dan berakhlak.</p>
                    <div className="space-y-2">
                      <EcoSectionHeader icon={Compass}>Tentang</EcoSectionHeader>
                      <p className="text-muted-foreground text-sm leading-relaxed">
                        Ekosistem pendidikan formal dan nonformal untuk membangun pemahaman agama yang benar, serta penguatan karakter dan intelektual.
                      </p>
                    </div>
                    <div className="space-y-3">
                      <EcoSectionHeader icon={School}>Program</EcoSectionHeader>
                      <EcoProgramList items={[
                        { icon: BookOpenCheck, text: "Kelas tematik Islam" },
                        { icon: Brain, text: "Pelatihan & workshop" },
                        { icon: BookMarked, text: "Kurikulum pembinaan" },
                        { icon: Award, text: "Program intensif" },
                      ]} />
                    </div>
                    <div className="space-y-3">
                      <EcoSectionHeader icon={Target}>Target Peserta</EcoSectionHeader>
                      <EcoTagGrid items={["Mahasiswa", "Profesional muda", "Orang tua", "Guru / Pendidik"]} />
                    </div>
                    <div className="space-y-3">
                      <EcoSectionHeader icon={BarChart3}>Outcome</EcoSectionHeader>
                      <EcoProgramList items={[
                        { icon: Medal, text: "Sertifikat program" },
                        { icon: Network, text: "Alumni network" },
                        { icon: Lightbulb, text: "Proyek pembelajaran" },
                      ]} />
                    </div>
                    <SharedCTA onDonate={() => setEcoDonationView(true)} />
                  </div>
                </>
              ) : (
                <DonationView onBack={() => setEcoDonationView(false)} backLabel="Thuba Pendidikan" />
              )}
            </DialogContent>
          </Dialog>

          {/* Thuba Kidz */}
          <Dialog open={activeEcoDialog === 'kidz'} onOpenChange={(open) => !open && closeEcoDialog()}>
            <DialogContent className="glass-card border-gold/30 max-w-lg overflow-y-auto max-h-[90vh]">
              {!ecoDonationView ? (
                <>
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-serif font-bold text-gold text-center">Thuba Kidz</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-6 px-1">
                    <p className="text-center text-muted-foreground italic">Belajar Islam itu menyenangkan!</p>
                    <div className="space-y-2">
                      <EcoSectionHeader icon={Compass}>Tentang</EcoSectionHeader>
                      <p className="text-muted-foreground text-sm leading-relaxed">
                        Program khusus anak usia TK dan SD untuk menumbuhkan keimanan, akhlak mulia, dan kecintaan terhadap Al-Qur'an sejak usia dini melalui metode belajar yang seru dan interaktif.
                      </p>
                    </div>
                    <div className="space-y-3">
                      <EcoSectionHeader icon={Palette}>Perencanaan Program</EcoSectionHeader>
                      <EcoProgramList items={[
                        { icon: BookOpenCheck, text: "Tahsin & tahfidz Al-Qur'an dasar" },
                        { icon: Palette, text: "Kelas seni & kreativitas Islami" },
                        { icon: Puzzle, text: "Permainan & kuis edukatif" },
                        { icon: Tent, text: "Outing & kegiatan alam" },
                      ]} />
                    </div>
                    <div className="space-y-3">
                      <EcoSectionHeader icon={Users}>Untuk Siapa</EcoSectionHeader>
                      <EcoTagGrid items={["Anak TK (usia 4–6 tahun)", "Anak SD (usia 7–12 tahun)", "Orang tua yang peduli pendidikan Islami anak"]} cols={1} />
                    </div>
                    <SharedCTA onDonate={() => setEcoDonationView(true)} />
                  </div>
                </>
              ) : (
                <DonationView onBack={() => setEcoDonationView(false)} backLabel="Thuba Kidz" />
              )}
            </DialogContent>
          </Dialog>

          {/* Thuba Kemitraan */}
          <Dialog open={activeEcoDialog === 'kemitraan'} onOpenChange={(open) => !open && closeEcoDialog()}>
            <DialogContent className="glass-card border-gold/30 max-w-lg overflow-y-auto max-h-[90vh]">
              {!ecoDonationView ? (
                <>
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-serif font-bold text-gold text-center">Thuba Kemitraan</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-6 px-1">
                    <p className="text-center text-muted-foreground italic">Berkolaborasi untuk dampak yang lebih luas.</p>
                    <div className="space-y-2">
                      <EcoSectionHeader icon={Compass}>Tentang</EcoSectionHeader>
                      <p className="text-muted-foreground text-sm leading-relaxed">
                        Ruang kolaborasi antara Thuba Project dengan sekolah, masjid, komunitas, dan perusahaan untuk menjalankan program bersama.
                      </p>
                    </div>
                    <div className="space-y-3">
                      <EcoSectionHeader icon={Link2}>Bentuk Kerja Sama</EcoSectionHeader>
                      <EcoProgramList items={[
                        { icon: BookOpen, text: "Program edukasi bersama" },
                        { icon: Mic, text: "Event & kajian kolaboratif" },
                        { icon: Megaphone, text: "Media partner" },
                        { icon: Briefcase, text: "CSR & sponsorship" },
                      ]} />
                    </div>
                    <div className="space-y-3">
                      <EcoSectionHeader icon={BarChart3}>Keuntungan Mitra</EcoSectionHeader>
                      <EcoProgramList items={[
                        { icon: Users, text: "Exposure komunitas" },
                        { icon: PenTool, text: "Kolaborasi konten" },
                        { icon: Heart, text: "Dampak sosial nyata" },
                      ]} />
                    </div>
                    <div className="space-y-3">
                      <a
                        href="https://wa.me/6282310001447?text=Assalamu'alaikum%2C%20kami%20tertarik%20untuk%20menjalin%20kerja%20sama%20kemitraan%20dengan%20Thuba%20Project.%20Mohon%20informasi%20lebih%20lanjut%20mengenai%20skema%20kolaborasi%20yang%20tersedia.%20Jazakumullahu%20khairan."
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <Button className="w-full bg-green-600 hover:bg-green-500 text-white font-bold py-5 rounded-xl shadow-lg shadow-green-900/20">
                          <Handshake className="w-4 h-4 mr-2" /> Ajukan Kerja Sama
                        </Button>
                      </a>
                    </div>
                    <div className="space-y-3 pt-4 border-t border-white/10">
                      <Button
                        onClick={() => setEcoDonationView(true)}
                        className="w-full bg-primary text-black hover:bg-[#F3E5AB] font-bold py-5 rounded-xl shadow-lg shadow-gold/20"
                      >
                        <Heart className="w-4 h-4 mr-2" /> Donasi
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <DonationView onBack={() => setEcoDonationView(false)} backLabel="Thuba Kemitraan" />
              )}
            </DialogContent>
          </Dialog>
        </div>
      </section>

      {/* Program Terkini */}
      <section className="py-24 bg-[#081c1a]">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-serif font-medium mb-12 text-white text-center">Program Terkini</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {/* Static Program 1 */}
            <div className="flex gap-6 items-start glass-card p-6 rounded-xl group hover:border-gold/40">
              <div className="flex-shrink-0 w-24 h-24 bg-gold/10 rounded-full flex items-center justify-center border border-gold/20">
                <Video className="w-12 h-12 text-gold" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-serif font-semibold text-white mb-2 group-hover:text-gold transition-colors">Live Streaming Kajian Rutin dan Tematik</h3>
                <p className="text-muted-foreground mb-4">Simak kajian ilmu yang disiarkan langsung melalui berbagai platform media sosial Thuba.</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-xs text-gold">
                    <Calendar className="w-3 h-3 mr-1" /> Jadwal Menyesuaikan
                  </div>
                </div>
              </div>
            </div>

            {/* Static Program 2 */}
            <div className="flex gap-6 items-start glass-card p-6 rounded-xl group hover:border-gold/40">
              <div className="flex-shrink-0 w-24 h-24 bg-gold/10 rounded-full flex flex-col items-center justify-center border border-gold/20 relative">
                <BookOpen className="w-9 h-9 text-gold mb-3" />
                <span className="absolute bottom-3 block text-[10px] uppercase tracking-wide text-muted-foreground font-bold">Soon</span>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-serif font-semibold text-white mb-2 group-hover:text-gold transition-colors">Kelas online Muamalah Kontemporer</h3>
                <p className="text-muted-foreground mb-4">Coming Soon. Program edukasi mendalam mengenai fiqih muamalah di era digital.</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-xs text-gold">
                    <Calendar className="w-3 h-3 mr-1" /> Segera Hadir
                  </div>
                </div>
              </div>
            </div>

            {/* Program Ramadhan */}
            <div className="flex gap-6 items-start glass-card p-6 rounded-xl group hover:border-gold/40 border border-gold/20 bg-gold/5">
              <div className="flex-shrink-0 w-24 h-24 bg-gold/10 rounded-full flex flex-col items-center justify-center border border-gold/20 relative">
                <Moon className="w-9 h-9 text-gold mb-3" />
                <span className="absolute bottom-3 block text-[10px] uppercase tracking-wide text-gold font-bold">Ramadhan</span>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-serif font-semibold text-white mb-2 group-hover:text-gold transition-colors">Program Ramadhan Thuba Project</h3>
                <p className="text-muted-foreground mb-4">Rangkaian program spesial Ramadhan: santunan dhuafa dan berbagi buka puasa.</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-xs text-gold">
                    <Calendar className="w-3 h-3 mr-1" /> Ramadhan 1447 H
                  </div>
                </div>
              </div>
            </div>

            {/* Kajian Offline */}
            <div className="flex gap-6 items-start glass-card p-6 rounded-xl group hover:border-gold/40">
              <div className="flex-shrink-0 w-24 h-24 bg-gold/10 rounded-full flex items-center justify-center border border-gold/20">
                <MapPin className="w-12 h-12 text-gold" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-serif font-semibold text-white mb-2 group-hover:text-gold transition-colors">Kajian Tematik dan Rutin Offline</h3>
                <p className="text-muted-foreground mb-4">Kajian tematik dan kajian rutin sekitar masjid yang berada di Jakarta, Bekasi, dan Kabupaten Bogor.</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-xs text-gold">
                    <Calendar className="w-3 h-3 mr-1" /> Jadwal Menyesuaikan
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Kegiatan dan Dokumentasi */}
      <section className="py-24 bg-[#0D2B29] relative overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="text-center mb-8">
            <h2 className="text-4xl font-serif font-medium mb-4 text-white">
              Kegiatan & Dokumentasi
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Momen berbagi kebaikan dan kebahagiaan dari berbagai program Thuba Project untuk kemaslahatan umat.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <Button
              onClick={() => setActiveGalleryTab('sosial')}
              className={`rounded-full px-6 py-2 transition-all ${activeGalleryTab === 'sosial' ? 'bg-primary text-black hover:bg-[#F3E5AB] font-bold shadow-[0_0_15px_rgba(212,175,55,0.3)]' : 'bg-white/5 text-muted-foreground hover:bg-white/10 hover:text-white border border-white/10'}`}
            >
              <Heart className="w-4 h-4 mr-2" /> Thuba Sosial
            </Button>
            <Button
              onClick={() => setActiveGalleryTab('dawah')}
              className={`rounded-full px-6 py-2 transition-all ${activeGalleryTab === 'dawah' ? 'bg-primary text-black hover:bg-[#F3E5AB] font-bold shadow-[0_0_15px_rgba(212,175,55,0.3)]' : 'bg-white/5 text-muted-foreground hover:bg-white/10 hover:text-white border border-white/10'}`}
            >
              <BookOpen className="w-4 h-4 mr-2" /> Thuba Da'wah
            </Button>
          </div>
          
          {/* Thumbnail Grid - Sosial */}
          {activeGalleryTab === 'sosial' && (
            <div className="flex gap-2 sm:gap-3 justify-center animate-fade-in-up overflow-x-auto pb-2 hide-scrollbar">
              {sosialImages.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => openGalleryLightbox(sosialImages, idx)}
                  className="group relative rounded-lg overflow-hidden cursor-pointer focus:outline-none focus:ring-2 focus:ring-gold/50 flex-shrink-0 w-[100px] h-[100px] sm:w-[140px] sm:h-[140px] lg:w-[160px] lg:h-[160px]"
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-0 left-0 right-0 p-2 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <p className="text-white text-[10px] sm:text-xs font-medium line-clamp-1">{img.alt}</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Thumbnail Grid - Da'wah */}
          {activeGalleryTab === 'dawah' && (
            <div className="flex gap-2 sm:gap-3 justify-center animate-fade-in-up overflow-x-auto pb-2 hide-scrollbar">
              {dawahImages.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => openGalleryLightbox(dawahImages, idx)}
                  className="group relative rounded-lg overflow-hidden cursor-pointer focus:outline-none focus:ring-2 focus:ring-gold/50 flex-shrink-0 w-[100px] h-[100px] sm:w-[140px] sm:h-[140px] lg:w-[160px] lg:h-[160px]"
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-0 left-0 right-0 p-2 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <p className="text-white text-[10px] sm:text-xs font-medium line-clamp-1">{img.alt}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Gallery Lightbox */}
      {galleryLightbox && createPortal(
        <div
          className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center"
          onClick={closeGalleryLightbox}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* Close button */}
          <button
            onClick={closeGalleryLightbox}
            className="absolute top-4 right-4 z-50 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-white/20 transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>

          {/* Counter */}
          <div className="absolute top-4 left-4 z-50 text-white/70 text-sm font-medium bg-black/40 backdrop-blur-sm px-3 py-1.5 rounded-full">
            {galleryLightbox.idx + 1} / {galleryLightbox.images.length}
          </div>

          {/* Prev button */}
          <button
            onClick={(e) => { e.stopPropagation(); galleryPrev(); }}
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-50 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-white/20 transition-colors"
          >
            <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </button>

          {/* Image */}
          <div className="w-full h-full flex items-center justify-center px-12 sm:px-20 py-16" onClick={(e) => e.stopPropagation()}>
            <img
              key={galleryLightbox.idx}
              src={galleryLightbox.images[galleryLightbox.idx].src}
              alt={galleryLightbox.images[galleryLightbox.idx].alt}
              className="max-w-full max-h-full object-contain rounded-lg animate-fade-in-up select-none"
              draggable={false}
            />
          </div>

          {/* Next button */}
          <button
            onClick={(e) => { e.stopPropagation(); galleryNext(); }}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-50 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-white/20 transition-colors"
          >
            <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </button>

          {/* Caption */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-50 bg-black/50 backdrop-blur-sm px-4 py-2 rounded-full max-w-[80%] text-center">
            <p className="text-white/90 text-sm font-medium truncate">{galleryLightbox.images[galleryLightbox.idx].alt}</p>
          </div>
        </div>,
        document.body
      )}

      {/* Slider Infografis Beranda */}
      <section className="py-16 bg-[#081c1a] border-t border-white/5">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-serif font-bold text-white">Flayer Dakwah Terbaru</h2>
            <Link to="/belajar-islam/infografis" className="text-gold hover:text-white transition-colors flex items-center text-sm">
              Lihat Semua <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </div>
          <style>
            {`
              .hide-scrollbar::-webkit-scrollbar { display: none; }
              .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
            `}
          </style>

          <div
            className="relative"
            onMouseEnter={() => setIsFlyerHovered(true)}
            onMouseLeave={() => setIsFlyerHovered(false)}
          >
            {/* Navigasi Kiri */}
            <button
              onClick={scrollFlyerLeft}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-20 w-12 h-12 bg-[#0D2B29]/80 hover:bg-[#0D2B29] backdrop-blur-sm rounded-full flex items-center justify-center transition-all duration-300 border border-gold/40 shadow-[0_0_15px_rgba(212,175,55,0.2)] md:-translate-x-6 hover:scale-110"
            >
              <ChevronLeft size={24} className="text-gold" />
            </button>

            <div
              ref={flyerSliderRef}
              className="flex overflow-x-auto gap-6 pb-6 hide-scrollbar relative z-10"
            >
              {flyers.map((f, idx) => (
                <button
                  onClick={() => setFlyerLightbox({ idx })}
                  key={f.id}
                  className="min-w-[280px] md:min-w-[320px] group bg-[#0D2B29] rounded-2xl overflow-hidden border border-white/10 hover:border-gold/40 transition-all duration-300 hover:scale-[1.03] flex flex-col text-left cursor-pointer"
                >
                  <div className="relative h-48 w-full overflow-hidden flex-shrink-0">
                    <img src={f.src} alt={f.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0D2B29] via-transparent to-transparent opacity-80" />
                  </div>
                  <div className="p-5 flex-1 flex flex-col justify-between -mt-8 relative z-10">
                    <span className="text-xs font-bold tracking-wider uppercase mb-2 inline-block px-3 py-1 bg-black/50 backdrop-blur-md rounded-full self-start" style={{ color: f.color }}>{f.tag}</span>
                    <h3 className="text-white text-base font-medium leading-snug group-hover:text-gold transition-colors line-clamp-2 mt-2">{f.title}</h3>
                  </div>
                </button>
              ))}
            </div>

            {/* Navigasi Kanan */}
            <button
              onClick={scrollFlyerRight}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-20 w-12 h-12 bg-[#0D2B29]/80 hover:bg-[#0D2B29] backdrop-blur-sm rounded-full flex items-center justify-center transition-all duration-300 border border-gold/40 shadow-[0_0_15px_rgba(212,175,55,0.2)] md:translate-x-6 hover:scale-110"
            >
              <ChevronRight size={24} className="text-gold" />
            </button>
          </div>
        </div>
      </section>

      {/* Flayer Lightbox Modal - rendered via Portal to bypass parent overflow/transform */}
      {flyerLightbox && createPortal(
        <div
          className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={() => setFlyerLightbox(null)}
        >
          <div className="relative max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={flyers[flyerLightbox.idx].src}
              alt={flyers[flyerLightbox.idx].title}
              className="w-full h-auto max-h-[60vh] object-contain rounded-2xl"
            />
            <div className="text-center mt-4 px-4">
              <h3 className="text-white text-xl font-serif font-bold mt-1">{flyers[flyerLightbox.idx].title}</h3>
              {flyers[flyerLightbox.idx].desc && (
                <div className="mt-4 text-white/80 text-sm md:text-base text-left max-w-2xl mx-auto whitespace-pre-line overflow-y-auto max-h-48 pr-2">
                  {flyers[flyerLightbox.idx].desc}
                </div>
              )}
              <Link
                to="/belajar-islam/infografis"
                onClick={() => setFlyerLightbox(null)}
                className="inline-flex items-center gap-2 mt-6 text-gold hover:text-white transition-colors text-sm font-medium border-b border-gold hover:border-white pb-0.5"
              >
                Lihat Semua Flayer <ArrowRight size={14} />
              </Link>
            </div>
            {/* Close */}
            <button onClick={() => setFlyerLightbox(null)} className="absolute top-3 right-3 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors">
              <X size={20} className="text-white" />
            </button>
            {/* Prev */}
            <button onClick={() => setFlyerLightbox(lb => ({ idx: (lb.idx - 1 + flyers.length) % flyers.length }))} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors">
              <ChevronLeft size={20} className="text-white" />
            </button>
            {/* Next */}
            <button onClick={() => setFlyerLightbox(lb => ({ idx: (lb.idx + 1) % flyers.length }))} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors">
              <ChevronRight size={20} className="text-white" />
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Dalil Section */}
      <section className="py-16 relative flex items-center justify-center bg-[#081c1a]">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/arabesque.png')] opacity-5"></div>
        <div className="container mx-auto px-6 text-center relative z-10 max-w-4xl">
          <h3 className="text-gold text-sm tracking-[0.2em] uppercase font-accent mb-8">Rasulullah ﷺ bersabda</h3>
          <h2 className="text-4xl md:text-6xl font-serif leading-relaxed mb-8 text-white font-normal" style={{ fontFamily: 'Amiri, serif' }}>
            من دَلَّ على خيرٍ فله مثلُ أجرِ فاعلِه
          </h2>
          <div className="space-y-4">
            <p className="text-xl md:text-2xl font-light text-muted-foreground italic">
              "Barangsiapa yang menunjuki kepada kebaikan maka dia akan mendapatkan pahala seperti pahala orang yang mengerjakannya"
            </p>
            <p className="text-gold/80 text-sm">(HR. Muslim no. 1893)</p>
          </div>
        </div>
      </section>

      {/* Filosofi Thuba */}
      <section className="py-32 bg-gradient-to-b from-[#081c1a] to-[#0D2B29] relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/arabesque.png')] opacity-5"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            {/* Left - Visual */}
            <div className="relative group flex justify-center py-10 scale-100 hover:scale-105 transition-transform duration-700">
              <style>
                {`
                    @keyframes float-tree {
                      0%, 100% { transform: translateY(0px); filter: drop-shadow(0 0 15px rgba(212, 175, 55, 0.2)); }
                      50% { transform: translateY(-15px); filter: drop-shadow(0 0 25px rgba(212, 175, 55, 0.5)); }
                    }
                    @keyframes frame-sway {
                      0%, 100% { transform: rotate(6deg) scale(1); border-color: rgba(212, 175, 55, 0.2); }
                      50% { transform: rotate(3deg) scale(1.05); border-color: rgba(212, 175, 55, 0.5); }
                    }
                    .tree-container {
                      animation: float-tree 5s ease-in-out infinite;
                    }
                    .frame-container {
                      animation: frame-sway 7s ease-in-out infinite;
                    }
                  `}
              </style>
              <div className="absolute inset-0 bg-gold/5 blur-[90px] rounded-full opacity-20 group-hover:opacity-40 transition-opacity duration-1000"></div>

              {/* Wrapper for float animation */}
              <div className="relative z-10 p-8 flex justify-center items-center">
                {/* Tilted Animated Frame */}
                <div className="absolute inset-0 border-2 border-gold/30 rounded-[3rem] frame-container bg-white/5 backdrop-blur-sm -z-10"></div>

                {/* Tree Image */}
                <div className="tree-container relative z-20">
                  <img
                    src="/thuba-tree-v3.png"
                    alt="Pohon Thuba"
                    className="w-full max-w-[300px] mx-auto brightness-0 invert"
                  />
                </div>
              </div>
            </div>

            {/* Right - Content */}
            <div className="order-1 md:order-2 space-y-6">
              <h2 className="text-4xl md:text-5xl font-serif font-medium text-white mb-6">
                Filosofi <span className="text-gold">Thuba</span>
              </h2>
              <div className="space-y-5 text-muted-foreground text-lg leading-relaxed">
                <p>
                  <span className="text-gold font-semibold">"Thuba"</span>—yakni pohon di surga bagi mereka yang tetap istiqomah meski dianggap asing oleh dunia.
                </p>
                <p>
                  Semangat inilah yang menjadi napas perjuangan Thuba Project: berikhtiar menebar cahaya Sunnah di tengah gelapnya fitnah, berharap kelak kita semua bisa bernaung di bawah rindangnya pohon Thuba di surga-Nya.
                </p>
              </div>
              <div className="pt-4 border-t border-gold/20 mt-8">
                <p className="text-sm text-gold/80 italic">
                  "Sesungguhnya Islam dimulai dengan keadaan asing, dan akan kembali asing sebagaimana awalnya. Maka beruntunglah orang-orang yang asing."
                </p>
                <p className="text-xs text-muted-foreground mt-2">(HR. Muslim)</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 bg-gold/5 border-y border-gold/10 relative overflow-hidden">
        <div className="container mx-auto px-6 text-center relative z-10">
          <div className="max-w-4xl mx-auto mb-12 space-y-10">
            <div className="space-y-6">
              <h3 className="text-2xl md:text-3xl font-serif font-semibold text-white">
                Pintu Kebaikan Tidak Selamanya Terbuka
              </h3>
              <div className="text-lg text-muted-foreground max-w-2xl mx-auto space-y-4">
                <p className="italic">
                  Seorang Tabi'in, Khâlid bin Ma'dân rahimahullah berpesan tegas:
                </p>
                <p className="italic border-l-4 border-gold pl-4">
                  "Bila telah terbuka bagi salah seorang dari kalian pintu kebaikan, hendaknya bersegera memasukinya. Sebab, sesungguhnya ia tidak tahu kapan pintu itu akan tertutup baginya."
                </p>
                <p>
                  Mumpung hati sedang tergerak, mumpung raga masih sehat, segera eksekusi niat baik itu. Jangan sampai kita baru ingin masuk saat pintunya sudah terkunci.
                </p>
                <p className="text-gold font-semibold">
                  Pintu kebaikan apa yang sedang terbuka di hadapanmu hari ini?
                </p>
              </div>
            </div>

            <div className="flex justify-center">
              <Dialog open={mainDonasiOpen} onOpenChange={(open) => open ? openMainDonasiDialog() : closeMainDonasiDialog()}>
                <DialogTrigger asChild>
                  <Button className="bg-primary text-black hover:bg-[#F3E5AB] rounded-full px-8 py-6 text-lg font-bold shadow-[0_0_20px_rgba(212,175,55,0.3)]">
                    Donasi Sekarang
                  </Button>
                </DialogTrigger>
                <DialogContent className="glass-card border-gold/30 max-w-md overflow-y-auto max-h-[90vh]">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-serif font-bold text-gold mb-4 text-center">Salurkan Donasi Terbaik Anda</DialogTitle>
                  </DialogHeader>
                  <div className="p-4 text-center space-y-6">
                    {/* QR Code / Flyer Image */}
                    <div className="rounded-xl overflow-hidden border border-gold/20 max-w-[180px] mx-auto">
                      <img
                        src="https://customer-assets.emergentagent.com/job_clean-menu-design/artifacts/wik7abko_image.png"
                        alt="QRIS Donasi"
                        className="w-full h-auto"
                      />
                    </div>

                    {/* Manual Transfer Details */}
                    <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                      <p className="text-sm text-muted-foreground mb-1">Transfer Manual</p>
                      <p className="text-sm text-gold font-medium mb-1">Bank Syariah Indonesia (BSI)</p>
                      <div className="flex items-center justify-center gap-2 mb-1">
                        <p className="text-2xl font-mono font-bold text-white tracking-wider select-all">7741114474</p>
                      </div>
                      <p className="text-xs text-muted-foreground">an. Yayasan Thuba Minal Jannah</p>
                    </div>

                    {/* Confirmation Button */}
                    <a
                      href="https://wa.me/6282310001447?text=Assalamu'alaikum%2C%20saya%20sudah%20transfer%20donasi%20untuk%20Thuba%20Project"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <Button className="bg-primary text-black hover:bg-[#F3E5AB] font-bold w-full rounded-full">
                        Konfirmasi via WhatsApp
                      </Button>
                    </a>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Jadilah bagian dari Thuba Project sebagai donatur, relawan, atau mitra kolaborasi dalam menyampaikan kebaikan dan menghadirkan manfaat melalui amal shalih.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
